import { APIError, APIErrorType } from '@vizit/api';
import { useAuth } from '@vizit/auth';
import { DEFAULT_RETAILER_REGION_ID, FEATURE_FLAGS, OTHER_RETAILER_REGION_ID } from '@vizit/constants';
import { useFeatureFlag } from '@vizit/feature-flags';
import { CreateANewProductDialog } from '@vizit/organisms';
import * as React from 'react';
import { useOrgRetailersWithOther } from '../../hooks/use-org-retailers-with-other';
import { useProductCategoryList, useProductNewDialog } from './product-new-dialog.hooks';

interface ProductNewDialogProps {
  /**
   * Whether the dialog is open
   *
   * @optional
   * @type {boolean}
   */
  isOpen?: boolean;
  /**
   * Callback to notify when the dialog open state changes
   * @param open - Whether the dialog should be open
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Whether to hide the trigger button
   * Set to true when controlled externally (e.g., by CallToActionCard)
   *
   * @optional
   * @type {boolean}
   */
  hideTrigger?: boolean;
  /**
   * The ID of the portfolio to use for the upload
   *
   * @optional
   * @type {string}
   */
  workspaceId?: string;
  /**
   * The ID of the organization to use for the upload
   *
   * @optional
   * @type {string}
   */
  organizationId?: string;
  /**
   * Whether to use an icon for the trigger button
   *
   * @optional
   * @type {boolean}
   */
  useIcon?: boolean;
  /**
   * The function to call when the PDP is successfully submitted
   *
   * @param pdpId - The ID of the PDP
   */
  onSuccessfulCreate: (pdpId: string) => void;
  /**
   * The function to call when the authentication error occurs
   *
   * @param error - The error that occurred
   */
  onAuthenticationError: (error: APIError) => void;
}

function ProductNewDialog(props: ProductNewDialogProps) {
  const {
    isOpen,
    onOpenChange,
    hideTrigger = false,
    workspaceId,
    organizationId,
    useIcon = false,
    onSuccessfulCreate,
    onAuthenticationError,
    ...other
  } = props;

  const isRetailerEnabled = useFeatureFlag(FEATURE_FLAGS.APRIL_SCORING_CHANGES, false);

  const {
    retailers,
    error: retailersError,
    showOther,
  } = useOrgRetailersWithOther({ organizationId, enabled: isRetailerEnabled });

  React.useEffect(() => {
    if (
      retailersError instanceof APIError &&
      (retailersError.type === APIErrorType.NotAuthenticated || retailersError.type === APIErrorType.NotAuthorized)
    ) {
      onAuthenticationError(retailersError);
    }
  }, [retailersError, onAuthenticationError]);

  const retailerSelectItems = isRetailerEnabled ? retailers : undefined;

  const { defaultRetailer } = useAuth();
  const defaultRetailerId = defaultRetailer ?? DEFAULT_RETAILER_REGION_ID;
  const [selectedRetailerId, setSelectedRetailerId] = React.useState<string>(defaultRetailerId);

  React.useEffect(() => {
    setSelectedRetailerId(defaultRetailerId);
  }, [defaultRetailerId]);

  // A form reset clears the pick, so the categories go back to the default retailer.
  const handleOnRetailerChange = React.useCallback(
    (retailerId: string) => setSelectedRetailerId(retailerId || defaultRetailerId),
    [defaultRetailerId],
  );

  const productCategoryComboBoxProps = useProductCategoryList({
    organizationId,
    workspaceId,
    retailerId: selectedRetailerId,
    onAuthenticationError,
  });

  const { hasOnSubmissionError, submissionError, uploadStatus, onFileStatusChange, onSubmit } = useProductNewDialog({
    workspaceId,
    organizationId,
    onSuccessfulCreate,
    onAuthenticationError,
  });

  return (
    <CreateANewProductDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      hideTrigger={hideTrigger}
      hasOnSubmissionError={hasOnSubmissionError}
      retailers={retailerSelectItems}
      otherRetailerId={showOther ? OTHER_RETAILER_REGION_ID : undefined}
      clearCategoryOnRetailerChange={showOther}
      submissionError={showOther ? submissionError : undefined}
      productCategoryComboBoxProps={productCategoryComboBoxProps}
      uploadStatus={uploadStatus}
      useIcon={useIcon}
      onFileStatusChange={onFileStatusChange}
      onRetailerChange={handleOnRetailerChange}
      onSubmit={onSubmit}
      {...other}
    />
  );
}

export { ProductNewDialog };
